
  import Order from "@/model/Order";
  import {defineComponent, onMounted, PropType, ref} from "vue";
  import {orderUuid} from "@/RouterUtils";
  import {DataTableHeader} from "vuetify";
  import {CashFlow} from "@/model/CashFlow";
  import StatisticService from "@/service/StatisticService";
  import {HeaderMutations} from "@/store/modules/header";
  import eventBus from "@/eventbus";
  import {DELETING_ENTRY, ENTRY_DELETED} from "@/plugins/i18n";
  import {MessageTypes} from "@/components/message-types";
  import store from "@/store/store";
  import StatisticOrderCashFlowDetailsDialog from "./StatisticOrderCashFlowDetailsDialog.vue";
  import Utils from "@/Utils";
  import {formatDate, toCurrency} from "@/filter";
  import {t} from "@/mixin/mixins";
  import ConfirmDialogImpl from "@/components/ConfirmDialog.vue";
  import StatisticServiceV2 from "@/service/StatisticServiceV2";
  import {OrderMonthlySummariesMonth} from "@/model/OrderMonthlySummaries";
  import {OrderSummariesMetrics} from "@/model/OrderSummaries";
  import moment from "moment";

  // The first month with statistics, so that the order's full history is loaded
  const FIRST_STATISTICS_MONTH = "2012-01";

  export default defineComponent({
    name: "StatisticOrder",
    components: {
      ConfirmDialog: ConfirmDialogImpl,
      StatisticOrderCashFlowDetailsDialog
    },
    props: {
      orders: {
        type: Array as PropType<Array<Order>>,
        default: () => new Array<Order>()
      }
    },
    setup(props, context) {
      const confirmDialog = ref<InstanceType<typeof ConfirmDialogImpl>>();

      const loadingMonths = ref<boolean>(false);
      const loadingCashFlows = ref<boolean>(false);
      const isOrderLoaded = ref<boolean>(false);

      const headersMonths: Array<DataTableHeader> = [
        {
          text: "Monat",
          align: "start",
          value: "month"
        },
        {
          text: "Stunden",
          align: "end",
          value: "metrics.minutes"
        },
        {
          text: "Kosten",
          align: "end",
          value: "metrics.wageCosts"
        },
        {
          text: "Kosten (inkl. LNK)",
          align: "end",
          value: "metrics.laborCosts"
        },
        {
          text: "Kosten (Fahrt)",
          align: "end",
          value: "metrics.travelCosts"
        },
        {
          text: "Kosten (Mitfahrer)",
          align: "end",
          value: "metrics.passengerCosts"
        },
        {
          text: "Zuschüsse",
          align: "end",
          value: "metrics.perDiems.total"
        },
        {
          text: "Einnahmen",
          align: "end",
          value: "metrics.earnings"
        },
        {
          text: "Ausgaben",
          align: "end",
          value: "metrics.expenses"
        },
        {
          text: "Gewinn",
          align: "end",
          value: "metrics.profit"
        },
        {
          text: "Stundenlohn (avg)",
          align: "end",
          value: "metrics.averageWage"
        }
      ];

      const headersCashFlow: Array<DataTableHeader> = [
        {
          text: "Datum",
          align: "start",
          value: "month"
        },
        {
          text: "Betrag",
          align: "end",
          value: "amount"
        },
        {
          text: "Typ",
          align: "start",
          value: "type"
        },
        {
          text: "Beschreibung",
          align: "start",
          value: "description"
        },
        {
          text: "",
          align: "end",
          value: "id",
          sortable: false,
          width: 80
        }
      ];

      const order = ref<Order>(new Order());
      const cashFlows = ref<Array<CashFlow>>(new Array<CashFlow>());
      const months = ref<Array<OrderMonthlySummariesMonth>>(new Array<OrderMonthlySummariesMonth>());
      const total = ref<OrderSummariesMetrics>();

      const cashFlowDialog = ref<InstanceType<typeof StatisticOrderCashFlowDetailsDialog>>();
      const createCashFlow = (): void => {
        cashFlowDialog.value?.createCashFlow(order.value, cashFlows.value, months.value, (cashFlow: CashFlow) => {
          cashFlows.value.push(cashFlow);
        });
      };

      const editCashFlow = (source: CashFlow) => {
        cashFlowDialog.value?.editCashFlow(order.value, source, (saved: CashFlow) => {
          Utils.replaceIfExists(cashFlows.value, source, saved);
        });
      };

      const loadMonths = (): Promise<void> => {
        loadingMonths.value = true;
        return StatisticServiceV2.getOrderMonthlySummaries(order.value.uuid!, FIRST_STATISTICS_MONTH, moment().format("YYYY-MM"))
          .then(response => {
            order.value.name = response.data.order.name;
            months.value = response.data.months;
            total.value = response.data.total;
          })
          .finally(() => {
            loadingMonths.value = false;
          });
      };

      const deleteCashFlow = (cashFlow: CashFlow) => {
        const title = "Eintrag löschen";
        const date = formatDate(cashFlow.month, "MMMM YYYY");
        const amount = toCurrency(cashFlow.amount);

        const text = `Soll der Eintrag <br><br><b>Datum</b>: ${date}<br><b>Betrag</b>: ${amount}<br><br> wirklich gelöscht werden?`;

        confirmDialog.value?.open(title, text)
          .then((confirmResult: boolean) => {
            if (confirmResult) {
              eventBus.$emit(MessageTypes.LOADING, t(DELETING_ENTRY));
              StatisticService.deleteCashFlow(order.value.uuid!, cashFlow.id!)
                .then(() => {
                  cashFlows.value = cashFlows.value.filter(item => item.id !== cashFlow.id);
                  loadMonths();
                  eventBus.$emit(MessageTypes.SUCCESS, t(ENTRY_DELETED));
                });
            }
          });
      };

      onMounted(() => {
        order.value.uuid = orderUuid.value;
        loadMonths()
          .then(() => {
            isOrderLoaded.value = true;
            store.commit("header/" + HeaderMutations.SET_TITLE, order.value.name);
          });

        loadingCashFlows.value = true;
        StatisticService.getCashFlows(orderUuid.value)
          .then(response => {
            cashFlows.value = response.data.cashFlows;
          })
          .finally(() => {
            loadingCashFlows.value = false;
          });
      });

      return {
        cashFlows,
        cashFlowDialog,
        createCashFlow,
        deleteCashFlow,
        editCashFlow,
        confirmDialog,
        headersCashFlow,
        headersMonths,
        loadingMonths,
        loadingCashFlows,
        isOrderLoaded,
        months,
        total
      };
    }
  });
